import type { StrategyMetric } from 'src/api/redux/strategy/types';
import { formatCurrency } from 'src/utils/formatCurrency/formatCurrency';

/**
 * Helper utilities for working with ChainedStrategy objects
 */

/**
 * Extracts the target strategy ID from a chained strategy
 * Used for API calls that require target_strategy.id
 *
 * @param chainedStrategy - The chained strategy object
 * @returns The target strategy ID or undefined if not available
 *
 * @example
 * const strategyId = getTargetStrategyId(selectedStrategy);
 * // Use in API call: `/api/strategies/${strategyId}/...`
 */
export const getTargetStrategyId = (
  chainedStrategy: MedScout.ChainedStrategy | null | undefined
): number | undefined => {
  return chainedStrategy?.target_strategy?.id;
};

export const getEngageStrategyId = (
  chainedStrategy: MedScout.ChainedStrategy | null | undefined
): number | undefined => {
  return chainedStrategy?.engage_strategies?.[0]?.id;
};

/**
 * Extracts the target strategy ID as a string from a chained strategy
 * Used for API calls that require target_strategy.id as a string
 *
 * @param chainedStrategy - The chained strategy object
 * @returns The target strategy ID as a string or undefined if not available
 *
 * @example
 * const strategyId = getTargetStrategyIdString(selectedStrategy);
 * // Use in API call with string parameter
 */
export const getTargetStrategyIdString = (
  chainedStrategy: MedScout.ChainedStrategy | null | undefined
): string | undefined => {
  const id = getTargetStrategyId(chainedStrategy);
  return id?.toString();
};

export const getEngageStrategyIdString = (
  chainedStrategy: MedScout.ChainedStrategy | null | undefined
): string | undefined => {
  const id = getEngageStrategyId(chainedStrategy);
  return id?.toString();
};

/**
 * Finds the one selected chain in Strategy Center. Per-strategy data (metrics, the
 * connector) only applies when exactly one chain is selected, so this returns `null`
 * for none or several selected chains, or when the chain is not in `chains`.
 *
 * @param selectedChainIds - the selected chain ids from `useStrategies`
 * @param chains - `results` of `useGetMyChainedStrategiesQuery`
 */
export const findSelectedChain = (
  selectedChainIds: number[] | undefined,
  chains: MedScout.ChainedStrategy[] | undefined
): MedScout.ChainedStrategy | null => {
  if (selectedChainIds?.length !== 1) return null;
  return chains?.find((chain) => chain?.id === selectedChainIds[0]) ?? null;
};

/**
 * Encounters-metric helpers for Strategy Center.
 *
 * Cohort-based strategy steps expose an `Encounters` metric alongside `Claims`,
 * with codes like `step_2_claims` / `step_2_encounters`. We never render a standalone
 * "Encounters" toggle; instead, when the `show-encounters-totals` flag is on, the
 * existing "Claims" toggle reads/requests the encounters sibling — and (MKT-521) is
 * relabeled "Encounters" so the label matches the data.
 */
const CLAIMS_SUFFIX = '_claims';
const ENCOUNTERS_SUFFIX = '_encounters';

/**
 * Removes the standalone `*_encounters` option from a display_options list so no
 * "Encounters" toggle renders (applied in both flag states).
 */
export const stripEncountersOptions = <T extends { code?: string }>(
  options: T[] | undefined
): T[] => (options ?? []).filter((option) => !option?.code?.endsWith(ENCOUNTERS_SUFFIX));

/**
 * Builds the display_options list to render for a metric: strips the standalone
 * `*_encounters` options (see {@link stripEncountersOptions}) and, when the flag is on,
 * relabels each `*_claims` option with its `*_encounters` sibling's BE-provided label
 * ("Encounters"), so the visible toggle matches the value it now reads.
 *
 * The option keeps its `*_claims` code as its identity, so `resolveEncountersMetricCode`
 * and the existing sort/request plumbing are unaffected — only the label changes.
 *
 * @param options - the metric's full display_options (claims and encounters entries)
 * @param showEncountersTotals - the LaunchDarkly flag value
 *
 * @example
 * const availableOptions = resolveEncountersDisplayOptions(
 *   metric.display_options,
 *   !!showEncountersTotals
 * );
 */
export const resolveEncountersDisplayOptions = <
  T extends { code?: string; label?: string }
>(
  options: T[] | undefined,
  showEncountersTotals: boolean
): T[] => {
  const visible = stripEncountersOptions<T>(options);
  if (!showEncountersTotals) return visible;

  const encountersLabels = new Map<string, string>(
    (options ?? [])
      .filter((option) => option?.code?.endsWith(ENCOUNTERS_SUFFIX))
      .map((option) => [option.code as string, option?.label ?? ''])
  );

  return visible.map((option) => {
    if (!option?.code?.endsWith(CLAIMS_SUFFIX)) return option;
    const sibling =
      option.code.slice(0, -CLAIMS_SUFFIX.length) + ENCOUNTERS_SUFFIX;
    const label = encountersLabels.get(sibling);
    return label ? { ...option, label } : option;
  });
};

/**
 * Resolves the metric code to read/request for a selected code. When the flag is on
 * and the selected code is a `*_claims` code whose `*_encounters` sibling exists among
 * `availableCodes`, returns the encounters code; otherwise returns the code unchanged.
 * (Never swaps patients or non-cohort metrics, and never requests encounters when the
 * sibling is absent.)
 */
export const resolveEncountersMetricCode = (
  code: string | undefined,
  availableCodes: Iterable<string>,
  showEncountersTotals: boolean
): string | undefined => {
  if (!showEncountersTotals || !code?.endsWith(CLAIMS_SUFFIX)) return code;
  const sibling = code.slice(0, -CLAIMS_SUFFIX.length) + ENCOUNTERS_SUFFIX;
  return new Set(availableCodes).has(sibling) ? sibling : code;
};

/** Display option types whose value is a list of locations, not a number to sort or show. */
const LOCATION_OPTION_TYPES = ['location', 'locations'];

/**
 * The display options a metric offers for sorting and as a row stat: the encounters
 * swap of {@link resolveEncountersDisplayOptions}, minus options without a code and
 * location options (their values are arrays, e.g. education's `institution`).
 *
 * @param options - the metric's full display_options
 * @param showEncountersTotals - the LaunchDarkly flag value
 */
export const getSortableDisplayOptions = <
  T extends { code?: string; label?: string; type?: string }
>(
  options: T[] | undefined,
  showEncountersTotals: boolean
): T[] =>
  resolveEncountersDisplayOptions(options, showEncountersTotals).filter(
    (option) => option?.code && !LOCATION_OPTION_TYPES.includes(option.type)
  );

/**
 * Formats a numeric metric value by its display option type: percent, currency and
 * boolean get their own format, a graduation year gets no thousands separator, and
 * everything else is a locale number.
 *
 * @param value - the raw metric value
 * @param optionType - the display option's `type`
 * @param code - the display option's code (a `graduation_year` code is shown plain)
 */
export const formatMetricValue = (
  value: number,
  optionType: string | undefined,
  code?: string
): string => {
  if (optionType === 'percent') {
    const percentValue = value <= 1 ? value * 100 : value;
    return `${Math.round(percentValue)}%`;
  }
  if (optionType === 'currency') return formatCurrency(value);
  if (optionType === 'boolean') return value ? 'Yes' : 'No';
  // Graduation year is typed as "volume" by the backend but should not get comma
  // formatting (2021, not "2,021"). Detect by option code.
  if (code?.includes('graduation_year')) return value.toString();
  return value.toLocaleString();
};

/** Where a Strategy results row reads its metric value, and what it is called. */
export interface MetricStatSource {
  metricId: number;
  /** The code to read from `metrics_v2[metricId]` (the encounters sibling when swapped). */
  code: string;
  label: string;
  /** The display option's `type`, which decides how the value is formatted. */
  type?: string;
}

/**
 * Picks the metric a Strategy results row shows as its stat: the sorted display option,
 * or the first one when the list is sorted by name or date. Options the sort dropdown
 * doesn't offer (see {@link getSortableDisplayOptions}) are skipped. Behind the encounters flag, a `*_claims` option is
 * relabeled "Encounters" and read from its `*_encounters` sibling, as the sort and
 * the table do.
 *
 * @param strategyMetrics - per-strategy metrics of the one selected chain
 * @param sortMetricCode - `sort_metric` of the active sort (the claims code as identity)
 * @param showEncountersTotals - the LaunchDarkly flag value
 * @returns `null` when no metric has a numeric display option
 */
export const resolveMetricStatSource = (
  strategyMetrics: StrategyMetric[] | undefined,
  sortMetricCode: string | null | undefined,
  showEncountersTotals: boolean
): MetricStatSource | null => {
  const candidates = (strategyMetrics ?? []).flatMap((metric) =>
    getSortableDisplayOptions(metric?.display_options, showEncountersTotals).map(
      (option) => ({ metric, option })
    )
  );
  const match =
    candidates.find(({ option }) => option.code === sortMetricCode) ??
    candidates[0];
  if (!match) return null;

  const allCodes = (match.metric.display_options ?? []).map(
    (option) => option?.code
  );
  return {
    metricId: match.metric.id,
    code: resolveEncountersMetricCode(
      match.option.code,
      allCodes,
      showEncountersTotals
    ),
    label: match.option.label,
    type: match.option.type,
  };
};
